/**
 * PostVisitReviewPrompt — Phase 2 patient-side review CTA.
 *
 * Surfaces a list of completed bookings the patient hasn't reviewed yet
 * (`GET /api/reviews/eligible`) and provides an inline 1-5★ + optional
 * comment form that POSTs to `/api/reviews/booking`. Each successful submit
 * removes the booking from the list and refreshes the eligible feed so the
 * UI never goes stale.
 *
 * Hidden completely (returns null) when there are zero eligible bookings —
 * the dashboard layout absorbs the empty case gracefully.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Star, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StarPicker = ({ value, onChange, testid }) => (
  <div className="flex items-center gap-1" data-testid={testid}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(n)}
        className="p-0.5 hover:scale-110 transition-transform"
        data-testid={`${testid}-${n}`}
        aria-label={`${n} star${n > 1 ? 's' : ''}`}
      >
        <Star
          className={`w-6 h-6 ${
            n <= value ? 'fill-amber-400 stroke-amber-500' : 'fill-none stroke-slate-300'
          }`}
        />
      </button>
    ))}
  </div>
);

const ReviewRow = ({ item, token, onSubmitted }) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async () => {
    if (rating < 1) {
      toast.error('Pick at least one star');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/reviews/booking`,
        { booking_id: item.booking_id, rating, comment: comment.trim() || null },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setDone(true);
      toast.success('Review submitted — thanks for the feedback');
      // Give the success animation a beat before purging the row from the list.
      setTimeout(() => onSubmitted(item.booking_id), 700);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit review');
      setSubmitting(false);
    }
  };

  return (
    <div
      className="border border-slate-200 rounded-lg p-4 flex flex-col gap-3"
      data-testid={`review-prompt-row-${item.booking_id}`}
    >
      <div className="flex items-start gap-3">
        {item.clinician_photo_url ? (
          <img
            src={item.clinician_photo_url}
            alt={item.clinician_name}
            className="w-10 h-10 rounded-full object-cover border border-slate-200"
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-500">
            {(item.clinician_name || 'C').slice(0, 1)}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-cn-text truncate">{item.clinician_name}</p>
          <p className="text-[11px] text-cn-text-muted">
            Visit completed · share how it went so other patients can find great care
          </p>
        </div>
      </div>

      {done ? (
        <div
          className="flex items-center gap-2 text-emerald-600 text-sm py-1"
          data-testid={`review-prompt-success-${item.booking_id}`}
        >
          <CheckCircle2 className="w-4 h-4" /> Thanks — your review is live.
        </div>
      ) : (
        <>
          <StarPicker
            value={rating}
            onChange={setRating}
            testid={`review-prompt-stars-${item.booking_id}`}
          />
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Optional — what stood out about this visit?"
            rows={2}
            maxLength={4000}
            className="w-full text-sm border border-slate-200 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-cn-primary/40"
            data-testid={`review-prompt-comment-${item.booking_id}`}
          />
          <div className="flex justify-end">
            <Button
              size="sm"
              disabled={submitting || rating < 1}
              onClick={handleSubmit}
              data-testid={`review-prompt-submit-${item.booking_id}`}
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> Submitting…
                </>
              ) : (
                'Submit review'
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default function PostVisitReviewPrompt({ token }) {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/reviews/eligible`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(data.eligible || []);
    } catch {
      // Eligible feed is best-effort — never block the dashboard on its failure.
    } finally {
      setLoaded(true);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSubmitted = (bookingId) => {
    setItems((prev) => prev.filter((i) => i.booking_id !== bookingId));
  };

  // Don't pop the empty-state card before the first fetch, and stay hidden if
  // there are no eligible bookings (dashboard reflows around our absence).
  if (!loaded || items.length === 0) return null;

  return (
    <section
      className="cn-card p-5 space-y-4"
      data-testid="post-visit-review-prompt"
    >
      <div>
        <h3 className="cn-display text-base text-cn-text flex items-center gap-2">
          <Star className="w-4 h-4 fill-amber-400 stroke-amber-500" />
          Review your recent visits
        </h3>
        <p className="text-[11px] text-cn-text-muted mt-1">
          {items.length} verified post-visit review{items.length === 1 ? '' : 's'} pending. Your feedback
          shapes the marketplace for every patient that comes after you.
        </p>
      </div>
      <div className="space-y-3">
        {items.map((it) => (
          <ReviewRow
            key={it.booking_id}
            item={it}
            token={token}
            onSubmitted={handleSubmitted}
          />
        ))}
      </div>
    </section>
  );
}
